import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, AlertTriangle, TrendingUp, Shield, ClipboardCheck, Calculator, Car, ArrowRight } from 'lucide-react';

const Advice = () => {
  const articles = [
    {
      id: 1,
      title: 'The Ultimate Used Car Buying Checklist',
      excerpt: 'Everything you need to check before signing on the dotted line. From paperwork to mechanical checks.',
      category: 'Buying Guide',
      readTime: '8 min',
      icon: ClipboardCheck,
      content: [
        'Check the vehicle\'s service history and ensure it\'s been regularly maintained',
        'Verify the registration papers match the VIN number on the car',
        'Do a CIPC check for outstanding finance',
        'Get an independent inspection before committing',
        'Test drive in various conditions – highway, hills, stop-start traffic',
        'Check all electronics, AC, and accessories work',
        'Look for signs of accident damage or respraying',
        'Negotiate based on market research, not emotions'
      ]
    },
    {
      id: 2,
      title: 'How to Spot a Clocked Car',
      excerpt: 'Odometer fraud is rampant in SA. Learn the warning signs that a car\'s mileage has been tampered with.',
      category: 'Fraud Prevention',
      readTime: '6 min',
      icon: AlertTriangle,
      content: [
        'Wear on steering wheel, pedals, and seats inconsistent with claimed mileage',
        'Service book gaps or missing stamps',
        'Digital odometer that looks newer than the car',
        'Suspiciously low mileage for the car\'s age',
        'Request a full vehicle history report',
        'Have an OBD diagnostic scan – some systems store true mileage',
        'Check brake disc wear – should match claimed kilometers',
        'Be suspicious of dealers who refuse inspections'
      ]
    },
    {
      id: 3,
      title: 'Understanding Vehicle Finance in South Africa',
      excerpt: 'Interest rates, balloon payments, terms – everything you need to know about financing your next car.',
      category: 'Finance',
      readTime: '10 min',
      icon: Calculator,
      content: [
        'Interest rates in SA range from prime (11.75%) to 15%+ for higher-risk profiles',
        'Shorter terms mean higher payments but less total interest',
        'Balloon payments reduce monthly costs but create a lump sum due at the end',
        'Always get pre-approved before shopping – it strengthens your negotiating position',
        'Factor in insurance, which is mandatory for financed vehicles',
        'Consider total cost of ownership, not just monthly payment',
        'Watch out for add-ons like credit life insurance padding the deal',
        'You can refinance or settle early, but check for penalties'
      ]
    },
    {
      id: 4,
      title: 'Why Vehicle Inspections Are Non-Negotiable',
      excerpt: 'The R1,500 inspection that could save you R50,000. Why every used car buyer needs one.',
      category: 'Inspections',
      readTime: '5 min',
      icon: Shield,
      content: [
        '40% of used cars have hidden defects – you can\'t see them all yourself',
        'Inspectors check 150+ points including engine, transmission, electronics',
        'OBD scans reveal error codes and true service history',
        'Paint thickness testing identifies accident repairs and resprays',
        'Compression testing reveals engine wear not visible during a test drive',
        'Independent inspectors have no incentive to hide problems',
        'A bad inspection report is valuable – it stops you making an expensive mistake',
        'Even dealer cars should be inspected – voetstoots applies to them too'
      ]
    },
    {
      id: 5,
      title: 'The Most Reliable Used Cars in South Africa',
      excerpt: 'Which brands and models have the best track record? Data-driven insights for smart buyers.',
      category: 'Buying Guide',
      readTime: '7 min',
      icon: TrendingUp,
      content: [
        'Toyota consistently ranks #1 for reliability – Corolla, Hilux, Fortuner',
        'Honda Jazz and Civic have excellent long-term reliability records',
        'VW Polo and Golf are popular but watch for DSG gearbox issues on older models',
        'Hyundai and Kia have improved dramatically – post-2015 models are solid',
        'German luxury cars (BMW, Mercedes, Audi) have higher maintenance costs',
        'Avoid first-year models of any new design – let others find the bugs',
        'Higher mileage from highway driving is often better than low city mileage',
        'Parts availability matters – popular models are cheaper to maintain'
      ]
    },
    {
      id: 6,
      title: 'Negotiating the Best Price: A Buyer\'s Guide',
      excerpt: 'Tactics and strategies to ensure you don\'t overpay for your next vehicle.',
      category: 'Buying Guide',
      readTime: '6 min',
      icon: Car,
      content: [
        'Research market values on AutoSearchSA before approaching any dealer',
        'Get pre-approved for finance so you negotiate on price, not payment',
        'Don\'t reveal your budget or show too much enthusiasm',
        'Point out any flaws or required repairs as negotiating leverage',
        'Be prepared to walk away – there are always other cars',
        'End of month is often the best time to buy (sales targets)',
        'Ask for extras (service, warranty) if they won\'t budge on price',
        'Get everything in writing before paying any deposit'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="container">
          <div className="max-w-3xl">
            <BookOpen className="w-12 h-12 mb-4 text-blue-300" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Car Buying Advice</h1>
            <p className="text-xl text-blue-100">
              Expert guides to help you make smart decisions when buying your next vehicle in South Africa.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <div key={article.id} className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition group">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <article.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs text-blue-600 font-medium">{article.category}</span>
                    <p className="text-xs text-gray-400">{article.readTime} read</p>
                  </div>
                </div>
                
                <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition">
                  {article.title}
                </h2>
                <p className="text-gray-600 text-sm mb-4">{article.excerpt}</p>
                
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Points:</h4>
                  <ul className="space-y-1">
                    {article.content.slice(0, 4).map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 md:p-12 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Find Your Perfect Car?</h2>
            <p className="text-gray-300 mb-6">
              Use what you've learned and start searching across multiple dealers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="px-8 py-4 bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold flex items-center justify-center gap-2 transition"
              >
                Search Cars
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/inspector"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-xl font-semibold transition"
              >
                Book an Inspection
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Advice;
